import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObjectsWithFilter, getObject, addObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import CreditNotePDF from './CreditNotePDF'
import { buildCreditNotePayload, convertDocumentAndUploadFile, createCreditNote } from '../../../utils/documentManager'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import { getCreditNoteFields } from './CreditNoteFields'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/creditNotes/edit',
  VIEW: '/creditNotes/view',
  ADD: '/creditNotes/add'
}

const EditCreditNote = () => {
  const history = useNavigate()
  const [creditNote, setCreditNote] = useState({ lines: [] })
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [isSubmit, setIsSubmit] = useState(false)
  const { user } = useAuth()
  const location = useLocation()
  const [settings, setSettings] = useState({})
  const path = location.pathname
  const creditNoteId = location.state?.id

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouvel Avoir' : `Avoir ${creditNote.creditNote_number}`
      }
    }),
    [creditNote, path]
  )

  const setLines = useCallback((newLines) => setCreditNote((prev) => ({ ...prev, lines: newLines })), [])

  const creditNoteQuery =
    mode === 'edit' ? { key: 'creditNote', fn: getObject, args: ['creditNotes', creditNoteId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    creditNoteQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'creditNotes',
    mode,
    id: creditNoteId,
    user,
    settingsKey: 'creditNote_number',
    updateCounter: false,
    addFn: addObject,
    editFn: editObject,
    contractLineName: 'Invoices',
    args: [creditNote],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: async (res) => {
      setCreditNote(res.data)
      convertDocumentAndUploadFile(
        <CreditNotePDF creditNote_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `avoir_${res.data.creditNote_number}`,
        user,
        'creditNote',
        'creditNotes',
        res.data
      )
      const { id, invoice_id: invoiceId, lastModDateTime } = res.data

      if (!lastModDateTime) {
        try {
          const resE = await editObject('invoices', invoiceId, {
            status: 81,
            lastModBy: user.email
          })
        } catch {
          toast.error('Erreur lors de la mise à jour de la facture', { autoClose: 2000 })
        }
      }
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildCreditNotePayload
  })

  const memoizedFields = useMemo(() => getCreditNoteFields(user, location, setLines), [user, location, setLines])
  const options = useMemo(() => ({ fields: memoizedFields, data: creditNote }), [memoizedFields, creditNote])

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.settings) setSettings(fetched.settings)
      if (fetched.company) {
        setCompany(fetched.company)
        const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
        if (attach?.file?.data) {
          const blob = new Blob([new Uint8Array(attach.file.data)])
          const reader = new FileReader()
          reader.onloadend = () => {
            const base64 = reader.result?.toString().split(',')[1] ?? ''
            setImg(base64)
          }
          reader.readAsDataURL(blob)
        }
      }
      if (fetched.creditNote) setCreditNote(fetched.creditNote)
    }
  }, [fetched, loadingEntities])

  const totalAmount = useMemo(() => {
    return (creditNote.lines || []).reduce((sum, line) => {
      const qty = parseFloat(line.quantity) || 0
      const price = parseFloat(line.unit_price) || 0
      return sum + qty * price
    }, 0)
  }, [creditNote.lines])

  useEffect(() => {
    setCreditNote((prev) => ({ ...prev, total_amount_net: totalAmount }))
  }, [totalAmount])

  useEffect(() => {
    let isMounted = true

    const fetchInvoice = async () => {
      if (!creditNote?.invoice_id) return
      try {
        const res = await getObject('invoices', creditNote.invoice_id)
        const invoice = res.data
        if (!isMounted || !invoice) return

        setCreditNote((prev) => ({
          ...prev,
          customer_id: invoice.customer_id,
          lines: invoice.lines,
          summary: `Vous trouverez ci-dessous votre avoir sur la facture n° ${
            invoice.invoice_number
          } en date du ${dayjs(invoice.invoice_date).format('DD/MM/YYYY')}`
        }))
      } catch (err) {}
    }

    fetchInvoice()
    return () => {
      isMounted = false
    }
  }, [creditNote?.invoice_id])

  useEffect(() => {
    if (isSubmit) {
      const cleanedInvoice = {
        ...creditNote,
        lines: (creditNote.lines || []).map((line) => {
          const { id, ...rest } = line
          return {
            ...rest,
            priceBook_id: rest.priceBook_id < -1 ? -1 : rest.priceBook_id
          }
        })
      }
      saveEntity({ data: cleanedInvoice })
    }
  }, [isSubmit])
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={creditNote} />

      <Form
        options={options}
        mode='edit'
        setDataParent={setCreditNote}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditCreditNote
